import api from './api';
import {
  ENTITY_ID_URL_APPENDIX,
  FROM_DATE_URL_APPENDIX,
  GET_COMPOSITE_STATE_SERVICE_URL,
  TO_DATE_URL_APPENDIX,
} from '../api/lelyBackend/config';
import {
  setEndLoading,
  setStartLoading,
} from '../common/redux/loadManager/actions';
import { getLoadId } from '../common/redux/loadManager/methods';
import { CompositeStateData } from '../reducers/types/compositeStateTypes';
import { RootState } from '../reducers/rootReducer';
import { transformDateRangeToFarmTimeZone } from './farmTimeZoneDateTransform';

export type GetCompositeStateArgs = {
  entityId: string;
  startDate: string;
  endDate: string;
};

function composeCompositeStateServiceUrl(
  stateName: 'milking' | 'cleaning',
  entityId: string,
  startDate: string,
  endDate: string,
) {
  let compositeStateServiceUrl = `${GET_COMPOSITE_STATE_SERVICE_URL}/${stateName}?`;
  compositeStateServiceUrl += ENTITY_ID_URL_APPENDIX + entityId;
  compositeStateServiceUrl +=
    FROM_DATE_URL_APPENDIX + startDate + TO_DATE_URL_APPENDIX + endDate;
  return compositeStateServiceUrl;
}

export const compositeStateApi = api.injectEndpoints({
  endpoints: (build) => ({
    getCompositeState: build.query<CompositeStateData, GetCompositeStateArgs>({
      queryFn: async (arg, apiContext, _extraOptions, fetchWithBQ) => {
        const state = apiContext.getState() as RootState;
        const { startDate: transformedStartDate, endDate: transformedEndDate } =
          transformDateRangeToFarmTimeZone(state, {
            startDate: arg.startDate,
            endDate: arg.endDate,
          });

        const milkingUrl = composeCompositeStateServiceUrl(
          'milking',
          arg.entityId,
          transformedStartDate,
          transformedEndDate,
        );
        const milking = await fetchWithBQ(milkingUrl);
        if (milking.error) {
          return { error: milking.error };
        }
        const cleaningUrl = composeCompositeStateServiceUrl(
          'cleaning',
          arg.entityId,
          transformedStartDate,
          transformedEndDate,
        );
        const cleaning = await fetchWithBQ(cleaningUrl);
        if (cleaning.error) {
          return { error: cleaning.error };
        }
        return {
          data: {
            ...(milking.data as CompositeStateData),
            ...(cleaning.data as CompositeStateData),
          },
        };
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const loadId = getLoadId();
        dispatch(setStartLoading(loadId));
        try {
          await queryFulfilled;
        } catch {
          // Query errors are consumed through RTK Query hook state.
        } finally {
          dispatch(setEndLoading(loadId));
        }
      },
      providesTags: ['CompositeState'],
    }),
  }),
});

export const { useGetCompositeStateQuery, useLazyGetCompositeStateQuery } =
  compositeStateApi;
